import React, { useState, useEffect } from "react";
import { Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import api from '../services/api';

const CallbackRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    id: null,
    phone: ""
  });

  useEffect(() => {
    const fetchCallbackRequests = async () => {
      try {
        const response = await api.get('/callbacks');
        setRequests(response.data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
        console.error("Error fetching callback requests:", err);
      }
    };

    fetchCallbackRequests();
  }, []);

  const openDeleteModal = (id, phone) => {
    setDeleteModal({
      isOpen: true,
      id,
      phone
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      id: null,
      phone: ""
    });
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/callbacks/delete/${deleteModal.id}`);
      setRequests(requests.filter((req) => req.id !== deleteModal.id));
      closeDeleteModal();
    } catch (err) {
      console.error("Error deleting callback request:", err);
      setError("Failed to delete callback request");
      closeDeleteModal();
    }
  };

  if (loading) {
    return (
      <motion.div
        className="max-w-4xl mx-auto p-6 mt-10 bg-slate-900 rounded-xl text-slate-200 shadow-2xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="text-2xl font-bold mb-6">Callback Requests</h2>
        <div className="text-center py-10">Loading...</div>
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div
        className="max-w-4xl mx-auto p-6 mt-10 bg-slate-900 rounded-xl text-slate-200 shadow-2xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="text-2xl font-bold mb-6">Callback Requests</h2>
        <div className="text-center py-10 text-red-400">Error: {error}</div>
      </motion.div>
    );
  }

  return (
    <>
      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            className="bg-slate-800 rounded-lg p-6 max-w-md w-full text-white"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <h3 className="text-xl font-bold mb-4">Confirm Deletion</h3>
            <p className="mb-6">
              Are you sure you want to delete the callback request for{" "}
              <span className="font-semibold">{deleteModal.phone}</span>?
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={closeDeleteModal}
                className="px-4 py-2 rounded-md bg-slate-700 hover:bg-slate-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Main Content */}
      <motion.div
        className="max-w-4xl mx-auto p-6 mt-10 bg-slate-900 rounded-xl text-slate-200 shadow-2xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h2 className="text-2xl font-bold mb-6">Callback Requests</h2>

        {requests.length === 0 ? (
          <p className="text-slate-400">No callback requests.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-800 text-slate-400">
                <tr>
                  <th className="p-3">Phone Number</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((req) => (
                  <tr key={req.id} className="border-b border-slate-700 hover:bg-slate-800">
                    <td className="p-3">{req.phone}</td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          req.status === "Resolved"
                            ? "bg-green-700 text-green-100"
                            : "bg-yellow-700 text-yellow-100"
                        }`}
                      >
                        {req.status}
                      </span>
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() => openDeleteModal(req.id, req.phone)}
                        className="p-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                        aria-label="Delete request"
                      >
                        <Trash2 className="w-4 h-4 text-white" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </>
  );
};

export default CallbackRequests;